import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where, type Timestamp } from 'firebase/firestore';
import { Activity, AlertTriangle, Loader2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../utils/AuthContext';
import { db } from '../../../utils/firebase';
import SettingsSectionShell from '../SettingsSectionShell';

type UsageRow = {
  id: string;
  uid?: string | null;
  displayName?: string | null;
  crmRole?: string | null;
  crmGroup?: string | null;
  action?: string | null;
  endpoint?: string | null;
  agentId?: string | null;
  decision?: 'allow' | 'deny' | string | null;
  isWrite?: boolean;
  reason?: string | null;
  keyId?: string | null;
  keyKind?: string | null;
  sourceIp?: string | null;
  userAgent?: string | null;
  ts?: Timestamp | null;
};

function dayKey(ts?: Timestamp | null): string {
  if (!ts?.toDate) return 'unknown';
  const d = ts.toDate();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

const ApiUsageSettingsSection: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState<UsageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [weekOnly, setWeekOnly] = useState(true);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const weekAgo = new Date();
    weekAgo.setUTCDate(weekAgo.getUTCDate() - 7);
    const q = weekOnly
      ? query(
          collection(db, 'crmApiUsage'),
          where('ts', '>=', weekAgo),
          orderBy('ts', 'desc'),
          limit(2000),
        )
      : query(collection(db, 'crmApiUsage'), orderBy('ts', 'desc'), limit(2000));
    return onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<UsageRow, 'id'>) })));
        setError(null);
      },
      (err) => {
        console.error('[ApiUsage]', err);
        setError(err.message || 'Could not load API usage');
        setRows([]);
      },
    );
  }, [isAdmin, weekOnly]);

  const groups = useMemo(() => {
    const map = new Map<string, { calls: number; denies: number }>();
    for (const row of rows || []) {
      const g = row.crmGroup || row.crmRole || 'unknown';
      const cur = map.get(g) || { calls: 0, denies: 0 };
      cur.calls += 1;
      if (row.decision === 'deny') cur.denies += 1;
      map.set(g, cur);
    }
    return Array.from(map.entries())
      .map(([crmGroup, stats]) => ({ crmGroup, ...stats }))
      .sort((a, b) => b.calls - a.calls);
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows || [];
    if (groupFilter !== 'all') {
      list = list.filter((r) => (r.crmGroup || r.crmRole || 'unknown') === groupFilter);
    }
    if (selectedUid) list = list.filter((r) => r.uid === selectedUid);
    return list;
  }, [rows, groupFilter, selectedUid]);

  const usersInView = useMemo(() => {
    const map = new Map<string, { uid: string; name: string; calls: number; denies: number }>();
    for (const row of filtered) {
      const uid = row.uid || 'unknown';
      const cur = map.get(uid) || {
        uid,
        name: row.displayName || uid.slice(0, 8),
        calls: 0,
        denies: 0,
      };
      cur.calls += 1;
      if (row.decision === 'deny') cur.denies += 1;
      map.set(uid, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.calls - a.calls);
  }, [filtered]);

  const endpointsInView = useMemo(() => {
    const map = new Map<string, { label: string; calls: number; denies: number }>();
    for (const row of filtered) {
      const label = `${row.endpoint || row.agentId || 'unknown'} · ${row.action || ''}`;
      const cur = map.get(label) || { label, calls: 0, denies: 0 };
      cur.calls += 1;
      if (row.decision === 'deny') cur.denies += 1;
      map.set(label, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.calls - a.calls).slice(0, 40);
  }, [filtered]);

  const byDay = useMemo(() => {
    const map = new Map<string, { day: string; calls: number; denies: number }>();
    for (const row of filtered) {
      const day = dayKey(row.ts);
      const cur = map.get(day) || { day, calls: 0, denies: 0 };
      cur.calls += 1;
      if (row.decision === 'deny') cur.denies += 1;
      map.set(day, cur);
    }
    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [filtered]);

  const denied = useMemo(
    () => filtered.filter((r) => r.decision === 'deny').slice(0, 50),
    [filtered],
  );

  if (!isAdmin) return <Navigate to="/settings/account" replace />;

  return (
    <SettingsSectionShell
      title="API usage"
      description="Employee and staff CRM API calls by group — key id only, never the secret. Admin only."
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <label>
          <input type="checkbox" checked={weekOnly} onChange={(e) => setWeekOnly(e.target.checked)} />{' '}
          Last 7 days
        </label>
        <label>
          Group{' '}
          <select value={groupFilter} onChange={(e) => { setGroupFilter(e.target.value); setSelectedUid(null); }}>
            <option value="all">All groups</option>
            {groups.map((g) => (
              <option key={g.crmGroup} value={g.crmGroup}>
                {g.crmGroup} ({g.calls})
              </option>
            ))}
          </select>
        </label>
        {selectedUid && (
          <button type="button" className="fin-secondary-btn" onClick={() => setSelectedUid(null)}>
            Clear user filter
          </button>
        )}
      </div>

      {error && (
        <p style={{ color: '#a33' }}>
          <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {error}
        </p>
      )}
      {rows == null && !error && (
        <p>
          <Loader2 size={14} className="bp-spin" style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Loading usage…
        </p>
      )}

      {rows && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 20 }}>
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Calls</span>
              <span className="fin-kpi-value">{filtered.length}</span>
            </div>
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Denied</span>
              <span className="fin-kpi-value">{denied.length}</span>
            </div>
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Groups</span>
              <span className="fin-kpi-value">{groups.length}</span>
            </div>
          </div>

          <h3>
            <Activity size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            By CRM group
          </h3>
          <table className="settings-table" style={{ width: '100%', marginBottom: 20 }}>
            <thead>
              <tr>
                <th>Group</th>
                <th>Calls</th>
                <th>Denied</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.crmGroup}>
                  <td>
                    <button type="button" className="linkish" onClick={() => setGroupFilter(g.crmGroup)}>
                      {g.crmGroup}
                    </button>
                  </td>
                  <td>{g.calls}</td>
                  <td>{g.denies}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Users</h3>
          <table className="settings-table" style={{ width: '100%', marginBottom: 20 }}>
            <thead>
              <tr>
                <th>User</th>
                <th>Calls</th>
                <th>Denied</th>
              </tr>
            </thead>
            <tbody>
              {usersInView.map((u) => (
                <tr key={u.uid}>
                  <td>
                    <button type="button" className="linkish" onClick={() => setSelectedUid(u.uid)}>
                      {u.name}
                    </button>
                  </td>
                  <td>{u.calls}</td>
                  <td>{u.denies}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Endpoints / actions</h3>
          <table className="settings-table" style={{ width: '100%', marginBottom: 20 }}>
            <thead>
              <tr>
                <th>Endpoint · action</th>
                <th>Calls</th>
                <th>Denied</th>
              </tr>
            </thead>
            <tbody>
              {endpointsInView.map((e) => (
                <tr key={e.label}>
                  <td>{e.label}</td>
                  <td>{e.calls}</td>
                  <td>{e.denies}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>By day</h3>
          <table className="settings-table" style={{ width: '100%', marginBottom: 20 }}>
            <thead>
              <tr>
                <th>UTC day</th>
                <th>Calls</th>
                <th>Denied</th>
              </tr>
            </thead>
            <tbody>
              {byDay.map((d) => (
                <tr key={d.day}>
                  <td>{d.day}</td>
                  <td>{d.calls}</td>
                  <td>{d.denies}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Recent denied / errors</h3>
          {denied.length === 0 ? (
            <p>No denied calls in this view.</p>
          ) : (
            <ul>
              {denied.map((r) => (
                <li key={r.id}>
                  {(r.displayName || r.uid || 'unknown')} · {r.endpoint || r.agentId} · {r.action}{' '}
                  {r.reason ? `(${r.reason})` : ''} · key {r.keyId || '—'}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </SettingsSectionShell>
  );
};

export default ApiUsageSettingsSection;
